/**
 * useRemoveStorageFromPreInvoice - Hook para remover storages de prefacturas
 * 
 * Proporciona funcionalidad para remover un storage de una prefactura con manejo de errores
 * y notificaciones toast.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { ViewPreInvoicesApiAdapter } from '../adapters/ViewPreInvoicesApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';

export const useRemoveStorageFromPreInvoice = () => {
  const { t } = useTranslation('viewPreInvoicesModule');
  const { toast } = useToast();
  const [isRemoving, setIsRemoving] = useState(false);

  const removeStorageFromPreInvoice = async (
    storageId: number,
    reason?: string,
    onSuccess?: () => void
  ) => {
    setIsRemoving(true);

    try {
      console.log('🔍 useRemoveStorageFromPreInvoice - Removiendo storage:', { storageId, reason });

      const httpClient = createHttpClient(apiClientWH);
      const adapter = new ViewPreInvoicesApiAdapter(httpClient);
      const response = await adapter.removeStorageFromPreInvoice(storageId, reason);

      console.log('🔍 useRemoveStorageFromPreInvoice - Respuesta:', response);

      // 🎯 Mostrar toast de éxito
      toast({
        title: t('toast.remove_storage_success.title', 'Storage removido'),
        description: t('toast.remove_storage_success.description', 'El storage se ha removido exitosamente de la prefactura'),
        variant: 'default',
      });

      // 🎯 Ejecutar callback de éxito (refetch, etc.)
      onSuccess?.();
    } catch (error: any) {
      console.log('🔍 useRemoveStorageFromPreInvoice - Error:', error);

      // 🎯 Extraer mensaje de error
      let errorMessage = t('toast.remove_storage_error.unexpected', 'Error inesperado al remover el storage. Inténtalo de nuevo.');
      
      if (error?.response?.data) {
        const errorData = error.response.data;
        
        if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' 
            ? errorData.error 
            : errorData.error.message || errorMessage;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // 🎯 Mostrar error
      toast({
        title: t('toast.remove_storage_error.title', 'Error al remover'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsRemoving(false);
    }
  };

  return {
    removeStorageFromPreInvoice,
    isRemoving,
  };
};

